import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { loadAppConfig } from '@srichaitra/config';
import { hydrateTokens } from '@srichaitra/api';
import { AppProviders } from '@srichaitra/common';
import App from './app/app';
import { StartupError } from './app/StartupError';

/**
 * Application bootstrap.
 *
 * Two things must happen before the first render, in this order:
 *
 *   1. config/app.ini is fetched and parsed. Everything downstream — the API
 *      base URL, whether payloads are encrypted, which storage holds the
 *      session — reads from it, and a component that rendered first would be
 *      reading an empty object.
 *   2. Tokens left by a previous visit are loaded into memory. Doing this
 *      before the tree mounts is what lets a returning user land on the
 *      dashboard instead of watching the login screen flash past.
 *
 * A failure in step 1 is fatal and renders StartupError instead of the app. The
 * alternative — carrying on with defaults — produces an application that looks
 * like it works while pointing at the wrong server.
 */
async function bootstrap() {
  const root = ReactDOM.createRoot(document.getElementById('root'));

  try {
    await loadAppConfig();
    hydrateTokens();
  } catch (error) {
    root.render(
      <StrictMode>
        <StartupError detail={error.message} />
      </StrictMode>
    );
    return;
  }

  root.render(
    <StrictMode>
      <AppProviders>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </AppProviders>
    </StrictMode>
  );
}

bootstrap();
